import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, X, Sparkles, ArrowUpRight } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getAiPrompt } from '../../data/shareContent';
import { trackEvent } from '../../lib/analytics';

const OpenAIGlyph = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
    <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 22.05a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
  </svg>
);

const ClaudeGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="h-5 w-5" aria-hidden="true">
    <line x1="12" y1="3" x2="12" y2="21" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="5.6" y1="5.6" x2="18.4" y2="18.4" />
    <line x1="18.4" y1="5.6" x2="5.6" y2="18.4" />
  </svg>
);

const PerplexityGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v6M12 15v6M3 12h6M15 12h6" />
    <path d="M5.6 5.6l3 3M18.4 5.6l-3 3M5.6 18.4l3-3M18.4 18.4l-3-3" />
  </svg>
);

const FloatingAIRobot = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { theme } = useTheme();
  const dark = theme !== 'light';
  const prompt = getAiPrompt(pathname);
  const chatgptUrl = `https://chatgpt.com/?q=${encodeURIComponent(prompt)}`;
  const claudeUrl = `https://claude.ai/new?q=${encodeURIComponent(prompt)}`;
  const perplexityUrl = `https://www.perplexity.ai/search/?q=${encodeURIComponent(prompt)}`;

  const ask = (model) => trackEvent('ai_assistant_open', { model, page: pathname });

  const panelBg = dark ? 'border-white/10 bg-slate-900/95 text-white' : 'border-slate-200 bg-white/95 text-slate-900';
  const optBase = dark ? 'border-slate-700 bg-slate-800/70 hover:bg-slate-800' : 'border-slate-200 bg-slate-50 hover:bg-slate-100';

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-robot-panel"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className={`fixed bottom-24 right-5 z-[65] w-[300px] rounded-2xl border p-4 shadow-2xl backdrop-blur-xl sm:right-6 ${panelBg}`}
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 text-white">
                  <Sparkles className="h-4 w-4" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-sm font-black leading-tight">Ask AI about FlexiOss®</p>
                  <p className="text-[11px] text-slate-500">Evidence-based answers</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ai-robot-close" aria-label="Close AI assistant" className="text-slate-400 transition-colors hover:text-slate-200">
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="mb-4 text-xs leading-relaxed text-slate-500">
              Opens a new chat pre-loaded with a scientific question grounded in our published, peer-reviewed research.
            </p>

            <div className="space-y-2.5">
              <a
                href={chatgptUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => ask('chatgpt')}
                data-testid="ai-ask-chatgpt"
                className={`group flex items-center justify-between gap-3 rounded-xl border px-3.5 py-3 transition-all ${optBase}`}
              >
                <span className="flex items-center gap-2.5">
                  <span style={{ color: '#10A37F' }}><OpenAIGlyph /></span>
                  <span className="text-sm font-bold">Ask ChatGPT</span>
                </span>
                <ArrowUpRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href={claudeUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => ask('claude')}
                data-testid="ai-ask-claude"
                className={`group flex items-center justify-between gap-3 rounded-xl border px-3.5 py-3 transition-all ${optBase}`}
              >
                <span className="flex items-center gap-2.5">
                  <span style={{ color: '#D97757' }}><ClaudeGlyph /></span>
                  <span className="text-sm font-bold">Ask Claude</span>
                </span>
                <ArrowUpRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href={perplexityUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => ask('perplexity')}
                data-testid="ai-ask-perplexity"
                className={`group flex items-center justify-between gap-3 rounded-xl border px-3.5 py-3 transition-all ${optBase}`}
              >
                <span className="flex items-center gap-2.5">
                  <span style={{ color: '#20808D' }}><PerplexityGlyph /></span>
                  <span className="text-sm font-bold">Ask Perplexity</span>
                </span>
                <ArrowUpRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen((o) => !o)}
        data-testid="ai-robot-toggle"
        aria-label="Open AI assistant"
        aria-expanded={open}
        whileTap={{ scale: 0.92 }}
        animate={open ? {} : { y: [0, -5, 0] }}
        transition={open ? {} : { duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        className="fixed bottom-6 right-5 z-[55] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-[0_10px_30px_-6px_rgba(37,99,235,0.65)] ring-1 ring-white/20 transition-shadow hover:shadow-[0_14px_38px_-6px_rgba(37,99,235,0.85)] sm:right-6"
      >
        {!open && <span className="absolute inset-0 rounded-full bg-blue-500/40 motion-safe:animate-ping" style={{ animationDuration: '2.6s' }} />}
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }}>
              <X className="h-6 w-6" />
            </motion.span>
          ) : (
            <motion.span key="bot" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.18 }} className="relative">
              <Bot className="h-6 w-6" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </>
  );
};

export default FloatingAIRobot;
